import { useState, useEffect } from "react";
import GenericModal from "../../../components/buttons/genericModal";
import { backendPostWithoutRefresh } from "../../../helpers/callBackend";
import LoadingButton from '@mui/lab/LoadingButton';
import GenericDropdown from "../../../components/generic/genericDropdown";
import CsvUpload from "../../../components/imports/csvUpload";
import { errorMessage } from "../../../helpers/errorMessageFunc";
import { Add, Delete } from "@mui/icons-material";
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';

import PermissionGate from "../../../helpers/PermissionGate";

const columns = [
	{
		title: "Hierarchy Name",
		dataIndex: "hierarchyName",
		required: true
	},
	{
		title: "Tag Name",
		dataIndex: "tagName",
		required: true
	},
	{
		title: "Parent Tag Name",
		dataIndex: "parentTagName",
		required: false
	},
];

const AddCustomGroupButton = (props) => {
	const [visible, setVisible] = useState(false);
	const [inFlight, setInFlight] = useState(false);
	const [dropdownItems, setDropdownItems] = useState([]);
	const [csvModalVisible, setCsvModalVisible] = useState(false);
	const [csvData, setCsvData] = useState([]);
	const [loading, setLoading] = useState(false);

	const preview = csvData.map((obj) => ({
		hierarchyName: obj.hierarchyName,
		tagName: obj.tagName,
		parentTagName: obj.parentTagName
	}));

	useEffect(() => {
		let newDropdownItems = []

		newDropdownItems.push(
			<Button
				onClick={() => setCsvModalVisible(true)}
				style={{
					background: 'none',
					border: 'none',
					padding: 0,
					margin: 0,
					color: 'primary', // or your theme's link color
					textDecoration: 'none',
					// cursor: !props.tableData || props.tableData.length === 0 ? 'not-allowed' : 'pointer',
					fontSize: 'inherit',
					fontFamily: 'inherit',
					textTransform: 'none'
				}}
			// disabled={!props.tableData || props.tableData.length === 0}
			>
				Upload CSV
			</Button>
		)
		setDropdownItems(newDropdownItems)
	}, [])
	const [rows, setRows] = useState([{ name: "", description: "" }]);

	const handlePaste = (e, index) => {
		const pastedData = e.clipboardData.getData("text");
		const pastedRows = pastedData.split("\n").filter(Boolean);

		// Simple single-cell paste — allow default
		if (pastedRows.length === 1 && !pastedData.includes("\t")) {
			return;
		}

		e.preventDefault(); // Prevent normal paste behavior

		const newRowData = pastedRows.map((row) => {
			const columns = row.split("\t");
			return {
				name: columns[0]?.trim() || "",
				description: columns[1]?.trim() || "",
			};
		});

		setRows((prevRows) => {
			const updated = [...prevRows];

			// Overwrite starting at paste index
			for (let i = 0; i < newRowData.length; i++) {
				const rowIndex = index + i;
				if (updated[rowIndex]) {
					updated[rowIndex] = newRowData[i];
				} else {
					updated.push(newRowData[i]);
				}
			}
			return updated;
		});
	};

	const onFormSubmit = async () => {

		const filteredRows = rows.filter(row => row.name.trim() !== "");
		if (filteredRows.length === 0) return;

		setInFlight(true);

		for (let row of filteredRows) {
			try {
				const res = await backendPostWithoutRefresh(
					"/api/generic-tag/group/",
					{ ...row, group_type: props.groupType }
				);
				const newGroup = res?.data?.generic_group;
				props.handleAddSuccess(newGroup);
			} catch (error) {
				console.error(error);
			}
		}

		setVisible(false);
		setInFlight(false);
	};
	const addRow = () => setRows([...rows, { name: "", description: "" }]);

	const removeRow = (index) => {
		if (rows.length > 1) {
			setRows(rows.filter((_, i) => i !== index));
		}
	};

	const handleChange = (index, field, value) => {
		const updatedRows = [...rows];
		updatedRows[index][field] = value;
		setRows(updatedRows);
	};

	const resetForm = () => {
		setRows([{ name: "", description: "" }]);
	};

	const handleCsvUpload = () => {
		let isValid = true;
		const errors = [];

		const validatedData = preview.map((row, index) => {
			const hierarchyName = row["hierarchyName"]?.trim();
			const tagName = row["tagName"]?.trim();

			if (!hierarchyName || !tagName) {
				isValid = false;
				errors.push(`Row ${index + 1}: Missing required fields.`);

			}

			return {
				hierarchyName: hierarchyName || "",
				tagName: tagName || "",
				parentTagName: row["parentTagName"]?.trim() || "", // optional
			};
		});

		if (!isValid) {
			return errorMessage("Some rows have missing required fields. Please check and try again.");
		}

		const res = backendPostWithoutRefresh(
			"/api/generic-tag/multi/",
			{ tagData: validatedData, group_type: props.groupType });

		setCsvModalVisible(false)
	}

	return (
		<Box display="flex" justifyContent="center" alignItems="center" gap={1}>
			<PermissionGate
				requiredRole='dashboardEditor'
				readOnly={
					<Button
						sx={{ fontSize: 14, textTransform: "none" }}
						size="small"
						variant="contained"
						disabled
					>
						+ Add New Hierarchy
					</Button>
				}
				toolTipText='Only admins and dashboard editors may add new hierarchies'
				toolTipPlacement='left'
				spanSettings={{ style: { width: '100%' } }}
			>
				<GenericModal
					visible={visible}
					handleCancelModal={() => {
						setVisible(false);
						resetForm();
					}}
					showModal={() => {
						setVisible(true);
						resetForm();
					}}

					size="md"
					button={
						<Button
							sx={{ fontSize: 14, textTransform: "none" }}
							disableElevation
							fullWidth
							size="small"
							variant="contained"
							color="primary"
						>
							+ Add New Hierarchy
						</Button>
					}
					modalTitle="Add New Hierarchy"
					modalBody={
						<Stack direction="column" spacing={2}>
							{rows.map((row, index) => (
								<Stack direction="row" spacing={1} key={index}>
									<TextField
										fullWidth
										label="Name"
										placeholder="Hierarchy Name"
										value={row.name}
										onChange={(e) => handleChange(index, "name", e.target.value)}
										onPaste={(e) => handlePaste(e, index)}
										required
									/>
									<TextField
										fullWidth
										label="Description"
										placeholder="Description"
										value={row.description}
										onChange={(e) => handleChange(index, "description", e.target.value)}
									/>
									{rows.length > 1 && (
										<IconButton onClick={() => removeRow(index)} color="error">
											<Delete fontSize="large" />
										</IconButton>
									)}
								</Stack>
							))}
							<Button
								sx={{ fontSize: 14 }}
								startIcon={<Add />}
								variant="outlined"
								fullWidth
								onClick={addRow}
							>
								Add More
							</Button>
							<Button
								sx={{ fontSize: 14 }}
								disableElevation
								fullWidth
								size="small"
								variant="contained"
								color="primary"
								onClick={onFormSubmit}
								disabled={inFlight}
								startIcon={inFlight ? <CircularProgress size={20} /> : null}
							>
								Submit
							</Button>
						</Stack>
					}
				/>
			</PermissionGate>
			<PermissionGate requiredRole='dashboardEditor'>
				<GenericDropdown dropdownItems={dropdownItems} />
			</PermissionGate>
			<GenericModal
				visible={csvModalVisible}
				handleCancelModal={() => setCsvModalVisible(false)}
				modalTitle="Upload Hierarchy from CSV"
				modalBody={
					<Stack direction="column" spacing={2}>
						{/* CSV Upload form or logic here */}
						<p>Please upload a csv with the following columns.</p>
						<Stack direction="column" sx={{ ml: 2 }}>
							{columns.map((entry) => {
								return <Stack direction="row">
									{entry.dataIndex}: {entry.title} {!entry.required && " (Optional)"}
								</Stack>
							})}
						</Stack>

						<CsvUpload setCsvData={setCsvData} />
						{csvData.length > 0 && (
							<div style={{ margin: "10px 0" }}>
								<Typography variant="h5" sx={{ fontSize: 14, fontWeight: 'bold' }} gutterBottom>
									Are you sure to continue with these hierarchy?
								</Typography>
								<TableContainer component={Paper} sx={{ maxHeight: 400 }}>
									<Table stickyHeader size="small">
										<TableHead>
											<TableRow>
												{columns.map((column, index) => (
													<TableCell key={index} style={{ minWidth: column.minWidth || 100 }}>
														{column.title}
													</TableCell>
												))}
											</TableRow>
										</TableHead>
										<TableBody>
											{preview.map((row, rowIndex) => (
												<TableRow key={rowIndex}>
													{columns.map((column, colIndex) => (
														<TableCell key={colIndex}>
															{row[column.dataIndex] || "-"}
														</TableCell>
													))}
												</TableRow>
											))}
										</TableBody>
									</Table>
								</TableContainer>

								<LoadingButton
									loading={loading}
									onClick={handleCsvUpload}
									loadingPosition="start"
									disableElevation fullWidth size="small" variant="contained" color="primary"
									sx={{ fontSize: 14, marginTop: 2, width: '100%', textTransform: 'none' }}
								>
									Submit
								</LoadingButton>
							</div>
						)}
					</Stack>
				}
			/>
		</Box>
	);
};

export default AddCustomGroupButton;